import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Heading,
  Spinner,
  Center,
  Text,
  Badge,
  Grid,
  GridItem,
  Divider,
  Button,
} from "@chakra-ui/react";
import { secureFetch } from "../data/secureFetch";
const {
  formatCentsToDollars,
  formatDateTimeToLocalString,
} = require("../store/calc-helpers");

const AllDonationDetails = () => {
  const { donationId } = useParams();
  const [donation, setDonation] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDonation = async () => {
      try {
        const response = await secureFetch(`/api/refunds/detail/${donationId}`);
        if (response.ok) {
          const data = response;

          const fetchedDonation = {
            ...data.donation,
            amount: formatCentsToDollars(data.donation.donation_amount),
            original_fee: formatCentsToDollars(data.donation.original_fee),
            actual_fee: formatCentsToDollars(data.donation.actual_fee),
            platform_tip_amount: formatCentsToDollars(
              data.donation.platform_tip_amount,
            ),
            actual_charge_amount: formatCentsToDollars(
              data.donation.actual_charge_amount,
            ),
            profit_loss: formatCentsToDollars(data.donation.profit_loss),
            created_at: formatDateTimeToLocalString(data.donation.created_at),
            stripeDetails: {
              stripe_pm_id: data.donation.stripe_pm_id,
              stripe_pi_id: data.donation.stripe_pi_id,
              stripe_ch_id: data.donation.stripe_ch_id,
            },
          };

          setDonation(fetchedDonation);

          setRefunds(
            data.refunds.map((refund) => ({
              ...refund,
              requested_amount: formatCentsToDollars(refund.requested_amount),
              refunded_amount:
                refund.refunded_amount != null
                  ? formatCentsToDollars(refund.refunded_amount)
                  : "N/A",
              customer_refunded_amount:
                refund.customer_refunded_amount != null
                  ? formatCentsToDollars(refund.customer_refunded_amount)
                  : "N/A",
              organization_refunded_amount:
                refund.organization_refunded_amount != null
                  ? formatCentsToDollars(refund.organization_refunded_amount)
                  : "0.00",
              fees_refunded:
                refund.fees_refunded != null
                  ? formatCentsToDollars(refund.fees_refunded)
                  : "N/A",
              created_at: formatDateTimeToLocalString(refund.created_at),
              updated_at: formatDateTimeToLocalString(refund.updated_at),
            })),
          );
        } else {
          throw new Error("Failed to fetch donation details");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDonation();
  }, [donationId]);

  const getStatusBadge = (status) => {
    const normalizedStatus = `${status || ""}`.toLowerCase();
    switch (normalizedStatus) {
      case "succeeded":
        return <Badge colorScheme="green">Succeeded</Badge>;
      case "pending":
        return <Badge colorScheme="blue">Pending</Badge>;
      case "refund pending":
        return <Badge colorScheme="blue">Refund Pending</Badge>;
      case "refunded":
        return <Badge colorScheme="yellow">Refunded</Badge>;
      case "failed":
        return <Badge colorScheme="red">Failed</Badge>;
      default:
        return <Badge colorScheme="gray">{status || "Unknown"}</Badge>;
    }
  };

  if (loading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  if (error) {
    return <Box>Error: {error}</Box>;
  }

  if (!donation) {
    return <Box>No donation details available.</Box>;
  }

  return (
    <Box p={5} maxW="800px" mx="auto">
      <Heading mb={6}>Donation Details</Heading>
      <Button as={Link} to="/donations" mb={4}>
        Back to Donations List
      </Button>
      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <GridItem>
          <Text>
            <strong>ID:</strong> {donation.id}
          </Text>
          <Text>
            <strong>Amount:</strong> ${donation.amount}
          </Text>
          <Text>
            <strong>Email:</strong> {donation.email}
          </Text>
          <Text>
            <strong>Name:</strong> {donation.first_name} {donation.last_name}
          </Text>
          <Text>
            <strong>Date:</strong> {donation.created_at}
          </Text>
        </GridItem>
        <GridItem>
          <Text>
            <strong>Status:</strong> {getStatusBadge(donation.status)}
          </Text>
          <Text>
            <strong>Card Type:</strong> {donation.card_type}
          </Text>
          <Text>
            <strong>Original Fee:</strong> ${donation.original_fee}
          </Text>
          <Text>
            <strong>Actual Fee:</strong> ${donation.actual_fee}
          </Text>
          <Text>
            <strong>Platform Tip Amount:</strong> $
            {donation.platform_tip_amount}
          </Text>
          <Text>
            <strong>Platform Tip Option:</strong> {donation.platform_tip_option}
          </Text>
          <Text>
            <strong>Actual Charge Amount:</strong> $
            {donation.actual_charge_amount}
          </Text>
          <Text>
            <strong>Profit/Loss:</strong> ${donation.profit_loss}
          </Text>
          <Text>
            <strong>Stripe Payment Method ID:</strong>{" "}
            {donation.stripeDetails.stripe_pm_id}
          </Text>
          <Text>
            <strong>Stripe Payment Intent ID:</strong>{" "}
            {donation.stripeDetails.stripe_pi_id}
          </Text>
          <Text>
            <strong>Stripe Charge ID:</strong>{" "}
            {donation.stripeDetails.stripe_ch_id}
          </Text>
        </GridItem>
      </Grid>
      <Divider my={4} />
      <Heading size="md" mb={4}>
        Address Information
      </Heading>
      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        {donation.address_line_1 && (
          <GridItem>
            <Text>
              <strong>Address Line 1:</strong> {donation.address_line_1}
            </Text>
          </GridItem>
        )}
        {donation.address_line_2 && (
          <GridItem>
            <Text>
              <strong>Address Line 2:</strong> {donation.address_line_2}
            </Text>
          </GridItem>
        )}
        {donation.city && (
          <GridItem>
            <Text>
              <strong>City:</strong> {donation.city}
            </Text>
          </GridItem>
        )}
        {donation.state && (
          <GridItem>
            <Text>
              <strong>State:</strong> {donation.state}
            </Text>
          </GridItem>
        )}
        {donation.zip_code && (
          <GridItem>
            <Text>
              <strong>Zip Code:</strong> {donation.zip_code}
            </Text>
          </GridItem>
        )}
      </Grid>
      <Divider my={4} />
      <Heading size="md" mb={4}>
        Additional Information
      </Heading>
      {donation.note && (
        <Text>
          <strong>Note:</strong> {donation.note}
        </Text>
      )}
      {donation.phone_number && (
        <Text>
          <strong>Phone Number:</strong> {donation.phone_number}
        </Text>
      )}
      <Text>
        <strong>Donation Type:</strong> {donation.donation_type}
      </Text>
      <Divider my={4} />
      <Heading size="md" mb={4}>
        Refunds
      </Heading>
      <Button as={Link} to={`/refunds/${donation.id}`} mt={4} mb={4}>
        Create a Refund
      </Button>
      {refunds.length === 0 ? (
        <Text>No refunds available for this donation.</Text>
      ) : (
        refunds.map((refund) => (
          <Box key={refund.id} p={4} mb={4} borderWidth="1px" borderRadius="md">
            <Text>
              <strong>Refund ID:</strong> {refund.id}
            </Text>
            <Text>
              <strong>Requested Amount:</strong> ${refund.requested_amount}
            </Text>
            <Text>
              <strong>Fees Refunded:</strong> ${refund.fees_refunded}
            </Text>
            <Text>
              <strong>Status:</strong> {getStatusBadge(refund.status)}
            </Text>
            <Text>
              <strong>Created At:</strong> {refund.created_at}
            </Text>
            <Text>
              <strong>Updated At:</strong> {refund.updated_at}
            </Text>
          </Box>
        ))
      )}
    </Box>
  );
};

export default AllDonationDetails;
